import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  Skeleton,
  toaster,
} from '@optiaxiom/react';

import { opalAuthAtom } from '@frontend/atoms';
// import { ShadowDomContainer } from '@frontend/components/ShadowDomContainer/ShadowDomContainer';
import { ErrorBoundary } from '@frontend/components/error-boundary';
import { CMP_BASE_URL } from '@frontend/envConfig';
import { getOrRenewOptiIdToken } from '@frontend/oktaAuth';

import { useCmpModules } from '../hooks/useCmpModules';
import { useNotificationActions } from '../hooks/useNotificationActions';
import type { HitlTaskDialogState } from '../utils';

type HitlTaskDialogProps = {
  onClose: () => void;
  state: HitlTaskDialogState;
};

export function HitlTaskDialog({ state, onClose }: HitlTaskDialogProps) {
  const { markActionCompleted } = useNotificationActions();
  const { CmpEmbedProvider, EmbeddableHITL, isLoaded, error } = useCmpModules(!!state);
  const opalAuth = useAtomValue(opalAuthAtom);
  const orgSsoId = opalAuth?.getAuthData()?.cmpOrgSsoId ?? '';
  const [token, setToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState(false);

  useEffect(() => {
    if (!state) {
      setToken(null);
      setTokenError(false);
      return;
    }
    getOrRenewOptiIdToken()
      .then(setToken)
      .catch(() => setTokenError(true));
  }, [state]);

  const handleTokenExpired = useCallback(() => {
    getOrRenewOptiIdToken()
      .then(setToken)
      .catch(() => setTokenError(true));
  }, []);

  const notificationId = state?.notificationId;

  // The decision is only real once it is recorded, so the confirmation waits
  // for the write. A failure surfaces the mutation's own error toast instead.
  const complete = useCallback(
    (message: string, intent: 'success' | 'information') => {
      onClose();
      if (!notificationId) {
        toaster.create(message, { intent });
        return;
      }
      markActionCompleted.mutate(notificationId, {
        onSuccess: () => toaster.create(message, { intent }),
      });
    },
    [markActionCompleted, notificationId, onClose]
  );

  const commentId = state?.commentId || '';

  // EmbeddableHITL reports a submit and knows nothing of approve vs decline — the
  // decision lives inside the Proteus document. One card per notification, so the
  // submit is the whole answer.
  const handleSubmit = useCallback(() => {
    complete('Response submitted', 'success');
  }, [complete]);

  const title = state?.agentName ? `${state.agentName} — Approval Required` : 'Approval Required';

  const showContent = isLoaded && token && state && CmpEmbedProvider && EmbeddableHITL;

  const loadFailed = <div>Failed to load task. Please try again.</div>;

  return (
    <Dialog onOpenChange={open => !open && onClose()} open={!!state}>
      <DialogContent size="lg">
        <DialogHeader>{title}</DialogHeader>
        <DialogBody overflow="auto">
          {error || tokenError ? (
            loadFailed
          ) : showContent ? (
            !commentId ? (
              <div>This request has no form to fill in.</div>
            ) : (
              // The CMP remote is third-party code rendered inside our tree. A
              // throw during its render would otherwise unmount the whole app,
              // so contain it to this dialog body.
              <ErrorBoundary fallback={loadFailed}>
                {/* CmpEmbedProvider mutates global singletons and is documented
                    as one-per-page. */}
                <CmpEmbedProvider
                  baseUrl={CMP_BASE_URL}
                  onTokenExpired={handleTokenExpired}
                  orgSsoId={orgSsoId}
                  token={token}
                >
                  <EmbeddableHITL
                    commentId={commentId}
                    onSubmit={handleSubmit}
                    taskId={state.taskId}
                  />
                </CmpEmbedProvider>
              </ErrorBoundary>
            )
          ) : (
            <Skeleton h="384" w="full" />
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
